import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Plus, Building2, User as UserIcon } from "lucide-react";
import { useClients, displayName, ClientStatus, ClientType } from "@/api/clients";
import { useAuthStore } from "@/stores/authStore";
import CreateClientModal from "./CreateClientModal";

const STATUS_STYLE: Record<ClientStatus, string> = {
  ACTIVE: "bg-green-100 text-green-700",
  PROSPECT: "bg-blue-100 text-blue-700",
  INACTIVE: "bg-gray-100 text-gray-600",
  ARCHIVED: "bg-gray-100 text-gray-400",
};

const STATUS_LABEL: Record<ClientStatus, string> = {
  ACTIVE: "Activo",
  PROSPECT: "Prospecto",
  INACTIVE: "Inactivo",
  ARCHIVED: "Archivado",
};

export default function ClientsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<ClientStatus | "">("");
  const [clientType, setClientType] = useState<ClientType | "">("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useClients({
    search: search || undefined,
    status: status || undefined,
    clientType: clientType || undefined,
    page,
    pageSize: 10,
  });

  const clients = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Clientes</h1>
          <p className="text-sm text-gray-500">Expediente central: toda la operación de la firma cuelga de un cliente.</p>
        </div>
        {hasPermission("clients.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nuevo cliente
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            placeholder="Buscar por nombre, razón social, documento o correo..."
            className="input-field pl-9"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <select
          className="input-field w-auto"
          value={clientType}
          onChange={(e) => {
            setClientType(e.target.value as ClientType | "");
            setPage(1);
          }}
        >
          <option value="">Todos los tipos</option>
          <option value="PERSONA_NATURAL">Persona natural</option>
          <option value="PERSONA_JURIDICA">Persona jurídica</option>
        </select>
        <select
          className="input-field w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ClientStatus | "");
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          <option value="ACTIVE">Activo</option>
          <option value="PROSPECT">Prospecto</option>
          <option value="INACTIVE">Inactivo</option>
          <option value="ARCHIVED">Archivado</option>
        </select>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Documento</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Contacto</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Responsable</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && clients.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                  No hay clientes que coincidan con la búsqueda.
                </td>
              </tr>
            )}
            {clients.map((c) => (
              <tr key={c.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link to={`/clients/${c.id}`} className="flex items-center gap-2 font-medium text-gray-900 hover:text-brand-600">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                      {c.clientType === "PERSONA_JURIDICA" ? <Building2 size={15} /> : <UserIcon size={15} />}
                    </span>
                    {displayName(c)}
                  </Link>
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {c.documentType} {c.documentNumber}
                </td>
                <td className="px-4 py-3 text-gray-600">{c.email || c.phone || "—"}</td>
                <td className="px-4 py-3 text-gray-600">{c.assignedTo?.fullName ?? "Sin asignar"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[c.status]}`}>
                    {STATUS_LABEL[c.status]}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} clientes)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button
                className="btn-secondary"
                disabled={!!meta.totalPages && page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateClientModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
